/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect } from 'react';
import { ChevronUpIcon, ChevronDownIcon, UserIcon, CheckoutIcon } from '@resortpass/solar-icons';
import useOutsideClick from '@hooks/useOutsideClick';
import { store } from '@context/store';
import { cartIconClick } from '@events/globals';
import Link from 'next/link';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import { useRouter } from 'next/router';
import cn from '@helpers/className';
import ButtonLoginTransparent from '../ButtonLoginTransparent';

export default function LoginAndCartMenuItem({
  isLoginMenuOpen,
  setIsLoginMenuOpen,
  setIsSignUpClicked,
  onAuthButtonClick,
  isFixedBar,
  setShowDesktopMenu,
  isNewLoginExperience,
}: any) {
  const globalState = useContext(store);
  const { state } = globalState;
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;
  const router = useRouter();

  const onLoginButtonClick = () => {
    setIsLoginMenuOpen(!isLoginMenuOpen);
  };

  const [outsideClickRef] = useOutsideClick((e: any) => {
    if (e.target && !e.target.classList.contains('login-header')) {
      setIsLoginMenuOpen(false);
    }
  });

  const navigateToCheckoutConfirmation = () => {
    cartIconClick();

    if (state.numberOfCartItems > 0) {
      window.location.href = '/products/checkout-confirmation';
    }
  };

  // hide login menu on page scroll
  useEffect(() => {
    const scrollHandler = () => {
      if (isLoginMenuOpen) {
        setIsLoginMenuOpen(false);
      }
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [isLoginMenuOpen, setIsLoginMenuOpen]);

  return (
    <div className="flex items-center z-200 justify-center">
      <div className="flex items-center gap-x-3">
        <div className="relative">
          {isFixedBar ? (
            <button
              type="button"
              className={cn({
                'border border-solar-secondary rounded-solar-sm flex justify-center items-center px-spacing-sm py-2 desktop:px-spacing-md':
                  true,
                'font-rp-basetica text-solar-primary cursor-pointer': true,
                'bg-solar-button-secondary-hover': isLoginMenuOpen,
                'bg-solar-button-secondary-default': !isLoginMenuOpen,
              })}
              onClick={onLoginButtonClick}
            >
              {user ? (
                <UserIcon width="18px" height="20px" className="login-header" />
              ) : (
                <p className="mr-2 text-sm login-header d:text-base">Login</p>
              )}
              {isLoginMenuOpen ? (
                <ChevronUpIcon className="login-header" fill="#22272A" width="13px" height="13px" />
              ) : (
                <ChevronDownIcon
                  className="login-header"
                  fill="#ffffff"
                  width="13px"
                  height="13px"
                />
              )}
            </button>
          ) : (
            <ButtonLoginTransparent
              onClick={onLoginButtonClick}
              classes="login-header"
              isLoginMenuOpen={isLoginMenuOpen}
            />
          )}

          {isLoginMenuOpen && (
            <div
              className="absolute bg-white font-rp-basetica text-solar-primary -mt-1 rounded-solar-sm w-72 z-300 flex flex-col items-start shadow-login-card right-0"
              style={{ top: 'calc(100% + 10px)' }}
              ref={outsideClickRef}
            >
              <button
                type="button"
                className={`pl-5 pb-4 pt-4 cursor-pointer z-100 w-full text-left align-middle rounded-t-xl hover:bg-solar-surface-secondary ${
                  isNewLoginExperience ? 'border-b border-rp-gray-divider' : ''
                }`}
                onClick={() => {
                  if (user) {
                    router.push('/users/bookings');
                    return;
                  }
                  setIsSignUpClicked(false);
                  setShowDesktopMenu(false);
                  onAuthButtonClick(!!user);
                }}
              >
                {user ? 'My Account' : isNewLoginExperience ? 'Login or Sign Up' : 'Login'}
              </button>
              {user || (!isNewLoginExperience && !user) ? (
                <button
                  type="button"
                  className="pl-5 pb-3 cursor-pointer z-100 w-full text-left align-middle pt-2 border-b border-rp-gray-divider hover:bg-solar-surface-secondary"
                  onClick={() => {
                    setIsSignUpClicked(true);
                    onAuthButtonClick(!!user);
                    setShowDesktopMenu(false);
                    setIsLoginMenuOpen(false);
                  }}
                >
                  {user ? 'Log Out' : 'Sign Up'}
                </button>
              ) : null}
              <Link href={`${process.env.NEXT_PUBLIC_RAILS_APP_URL}/hotel_users/sign_in`}>
                <button
                  type="button"
                  className="pl-5 pb-4 cursor-pointer z-100 w-full text-left align-middle pt-3 hover:bg-solar-surface-secondary"
                  onClick={() => {
                    setShowDesktopMenu(false);
                  }}
                >
                  Hotel Login
                </button>
              </Link>
              <Link href="https://partners.resortpass.com/">
                <button
                  type="button"
                  className="pl-5 pb-4 cursor-pointer z-100 w-full text-left align-middle pt-2 rounded-b-xl hover:bg-solar-surface-secondary"
                  onClick={() => {
                    setShowDesktopMenu(false);
                  }}
                >
                  List My Hotel
                </button>
              </Link>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={navigateToCheckoutConfirmation}
          className="flex items-center border border-solar-secondary rounded-solar-sm self-stretch px-2 relative bg-solar-surface-primary"
        >
          <CheckoutIcon fill="#05102E" width="24px" height="24px" />

          {state.numberOfCartItems > 0 && (
            <div
              className="bg-solar-surface-brand-shadow text-white font-rp-basetica rounded-full flex justify-center items-center pt-px z-100 absolute h-4 w-4 text-11 -right-1 -top-1"
              id="header-cart-item-count"
            >
              {state.numberOfCartItems}
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
